import type { RowData, Table as TanStackTable } from "@tanstack/table-core";
import { Virtualizer } from "@tanstack/virtual-core";
import { useVirtualizer } from "@tanstack/react-virtual";
import { PropsWithChildren, useEffect } from "react";
import { cn } from "../../../utils/classes";
import styles from "../table.module.css";
import { containerGridStyles } from "../utils/grid";
import { TableBodyRow } from "./row";

type TableBodyProps<D extends RowData> = PropsWithChildren<{
  columnVirtualizer: Virtualizer<HTMLDivElement, HTMLTableCellElement>;
  table: TanStackTable<D>;
  tableContainerRef: React.RefObject<HTMLDivElement | null>;
  virtualPaddingLeft: number | undefined;
  virtualPaddingRight: number | undefined;
  virtualRows?: boolean;
  endContent?: React.ReactNode;
  onEndReached?: () => void;
  onRowClick?: (row: D) => void;
}>;

export function TableBody<D extends RowData>({
  columnVirtualizer,
  table,
  tableContainerRef,
  virtualPaddingLeft,
  virtualPaddingRight,
  virtualRows,
  endContent,
  onEndReached,
  onRowClick,
  children,
}: TableBodyProps<D>) {
  const { rows } = table.getRowModel();
  const rowVirtualizer = useVirtualizer<HTMLDivElement, HTMLTableRowElement>({
    count: rows.length,
    estimateSize: () => 48,
    getScrollElement: () => tableContainerRef.current,
    overscan: 8,
    enabled: virtualRows,
  });
  const virtualItems = virtualRows ? rowVirtualizer.getVirtualItems() : [];
  const lastVirtualIndex = virtualItems.at(-1)?.index;

  useEffect(() => {
    if (
      virtualRows &&
      onEndReached &&
      lastVirtualIndex !== undefined &&
      lastVirtualIndex >= rows.length - 10
    ) {
      onEndReached();
    }
  }, [lastVirtualIndex, onEndReached, rows.length, virtualRows]);

  const paddingTop = virtualItems[0]?.start ?? 0;
  const paddingBottom = virtualItems.length
    ? rowVirtualizer.getTotalSize() - (virtualItems.at(-1)?.end ?? 0)
    : 0;
  const renderedRowCount = virtualRows ? virtualItems.length : rows.length;

  const gridTemplateRows = [
    ...(paddingTop > 0 ? [`${paddingTop}px`] : []),
    ...Array.from({ length: renderedRowCount }, () => "min-content"),
    ...(paddingBottom > 0 ? [`${paddingBottom}px`] : []),
    ...(rows.length > 0 && endContent ? ["min-content"] : []),
  ].join(" ");

  return (
    <tbody
      style={{
        display: "grid",
        ...containerGridStyles,
        gridTemplateRows,
        height: "auto",
      }}
      className={cn("divide-y divide-gray-300 relative w-full", styles.body)}
    >
      {rows.length === 0 ? (
        <tr
          className="items-stretch "
          style={{
            ...containerGridStyles,
            contentVisibility: "auto",
            containIntrinsicSize: "auto",
            width: "100%",
            alignItems: "center",
          }}
        >
          <td className={styles.no_results}>{children}</td>
        </tr>
      ) : (
        <>
          {paddingTop > 0 && (
            <tr
              aria-hidden
              style={{ gridColumn: "1 / -1", height: paddingTop }}
            />
          )}
          {virtualRows
            ? virtualItems.map((virtualRow) => (
                <TableBodyRow
                  columnVirtualizer={columnVirtualizer}
                  key={rows[virtualRow.index].id}
                  row={rows[virtualRow.index]}
                  rowVirtualizer={rowVirtualizer}
                  virtualRow={virtualRow}
                  virtualPaddingLeft={virtualPaddingLeft}
                  virtualPaddingRight={virtualPaddingRight}
                  onRowClick={onRowClick}
                />
              ))
            : rows.map((row) => (
                <TableBodyRow
                  columnVirtualizer={columnVirtualizer}
                  key={row.id}
                  row={row}
                  virtualPaddingLeft={virtualPaddingLeft}
                  virtualPaddingRight={virtualPaddingRight}
                  onRowClick={onRowClick}
                />
              ))}
          {paddingBottom > 0 && (
            <tr
              aria-hidden
              style={{ gridColumn: "1 / -1", height: paddingBottom }}
            />
          )}
          {endContent && (
            <tr
              className="h-9 items-center"
              style={{ ...containerGridStyles, width: "100%" }}
            >
              <td className="col-[1/-1] flex h-9 items-center justify-center">
                {endContent}
              </td>
            </tr>
          )}
        </>
      )}
    </tbody>
  );
}
